import {Paginated, Stored} from "../utils/commons";
import {TLiteral, TUnion, Type} from "@sinclair/typebox";

export enum Role {
}

export interface User {
    id: number;
    externalId: string;
    email: string;
    role?: Role;
    billingProvider?: string;
    svixAppPortalUrl?: string;
}

export interface Credential {
    id: string;
}

export interface FullCredential extends Credential {
    secret: string;
}

export type ProductCredits = { availableQty: number, subscribedQty: number, consumedQtyExtra: number, isAllowed: boolean, consumedTotal: number };
export type UserCredits = {
    data_api: ProductCredits;
    webpage_api: ProductCredits;
    document_api: ProductCredits;
    timestamp_api: ProductCredits;
    document_simple_api: ProductCredits;
    document_simple_otp_api: ProductCredits;
    document_aes_api: ProductCredits;
    document_aes_api_preservation: ProductCredits;
};
export type OfficialProductType = keyof UserCredits;

export enum CallbackEvent {
    USER_CONCLUDED = "USER_CONCLUDED",
}

export enum CallbackResourceType {
    Interaction = "Interaction",
    Signature = "Signature"
}

export interface Callback {
    resourceType: CallbackResourceType;
    endpoint: string;
    onlyFinalStatuses: boolean;
    events?: CallbackEvent[];
}

export enum SignatureFont {
    CAVEAT = "Caveat",
    LEAGUE_SCRIPT = "LeagueScript",
    MRS_SAINT_DELAFIELD = "MrsSaintDelafield",
    SHADOWS_INTO_LIGHT = "ShadowsIntoLight",
    SUE_ELLEN_FRANCISCO = "SueEllenFrancisco",
    ALEX_BRUSH = "AlexBrush",
    ALLURA = "Allura",
    DANCING_SCRIPT_VARIABLE_FONT = "DancingScriptVariableFont",
    PARISIENNE = "Parisienne",
    STALEMATE = "Stalemate",
}

export type IntoStringUnion<T> = {[K in keyof T]: T[K] extends string ? TLiteral<T[K]>: never }

export function StringUnion<T extends string[]>(values: [...T]): TUnion<IntoStringUnion<T>> {
    return { enum: values } as any
}

export const signatureCustomisationsSchema = Type.Object({
    customLogoUrl: Type.Optional(Type.String({format: "uri", pattern: "^(http|https).*$"})),
    fullNameSignatureFont: Type.Optional(StringUnion(Object.values(SignatureFont))),
    signPage: Type.Optional(Type.Object({
        title: Type.Optional(Type.String()),
        showLogo: Type.Optional(Type.Boolean()),
    }, {additionalProperties: false})),
    otpSmsPage: Type.Optional(Type.Object({
        title: Type.Optional(Type.String()),
        subtitle: Type.Optional(Type.String()),
        showLogo: Type.Optional(Type.Boolean()),
    }, {additionalProperties: false})),
    outcomePage: Type.Optional(Type.Object({
        title: Type.Optional(Type.String()),
        titleRejected: Type.Optional(Type.String()),
        subtitle: Type.Optional(Type.String()),
        showLogo: Type.Optional(Type.Boolean()),
        customLink: Type.Optional(Type.Object({
            title: Type.String(),
            url: Type.String({format: "uri", pattern: "^(http|https).*$"}),
            redirectInSecs: Type.Optional(Type.Number({minimum: 3, maximum: 10})),
        }, {additionalProperties: false})),
    }, {additionalProperties: false})),
});

export const emailCustomisationSchema = Type.Object({
    subject: Type.Optional(Type.String()),
    preOTPText: Type.Optional(Type.String()),
    applicationName: Type.Optional(Type.String()),
    logoUrl: Type.Optional(Type.String({format: "uri", pattern: "^(http|https).*$"})),
});

export interface ArchetypeCreation {
    mandatoryTags?: Record<string, string>;
    formTags?: Record<string, string>;
    name?: string;
    smsCustomMessage?: string;
    signatureCustomisations?: object;
    emailCustomisation?: object;
}

export interface Archetype extends ArchetypeCreation {
    smsCustomMessageModeratedOn?: Date;
}

export interface UserState {
    user?: User;
    credentials?: Credential[];
    generatedCredential?: FullCredential;
    creatingCredential: boolean;
    loading: boolean;
    credits?: UserCredits;
    callbacks?: Paginated<Stored<Callback>>;
    archetypes?: Paginated<Stored<Archetype>>;
}
